import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { UserPlus, Mail, Lock, User, Building, Briefcase } from 'lucide-react'

const QuickSignup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    agency: '',
    position: '',
    user_type: 'government_employee'
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const { signUp, error } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      const { data, error } = await signUp(formData.email, formData.password, {
        name: formData.name,
        agency: formData.agency,
        position: formData.position,
        user_type: formData.user_type
      })
      
      if (!error && data.user) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      }
    } catch (err) {
      console.error('Signup error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center"
        >
          <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <UserPlus className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
          <p className="text-gray-600 mb-4">
            Your account has been created successfully. You can now sign in with your credentials.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to login page...
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="mx-auto h-12 w-12 bg-cyber-600 rounded-lg flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-display font-bold text-gov-800">
            Create Your Account
          </h2>
          <p className="mt-2 text-center text-sm text-gov-600">
            Set up your CyberGov account to get started
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gov-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gov-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                  placeholder="your.email@agency.gov"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gov-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gov-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                  placeholder="Enter a secure password"
                  minLength={6}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gov-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gov-400" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="agency" className="block text-sm font-medium text-gov-700 mb-1">
                Agency/Organization
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gov-400" />
                </div>
                <input
                  id="agency"
                  name="agency"
                  type="text"
                  required
                  value={formData.agency}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                  placeholder="Department of Technology"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gov-700 mb-1">
                Position/Title
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Briefcase className="h-5 w-5 text-gov-400" />
                </div>
                <input
                  id="position"
                  name="position"
                  type="text"
                  required
                  value={formData.position}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                  placeholder="IT Administrator"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="user_type" className="block text-sm font-medium text-gov-700 mb-1">
                User Type
              </label>
              <select
                id="user_type"
                name="user_type"
                value={formData.user_type}
                onChange={handleChange}
                className="block w-full px-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                <option value="government_employee">Government Employee</option>
                <option value="it_administrator">IT Administrator</option>
                <option value="security_officer">Security Officer</option>
                <option value="department_head">Department Head</option>
                <option value="compliance_officer">Compliance Officer</option>
              </select>
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-cyber-600 hover:bg-cyber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gov-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-cyber-600 hover:text-cyber-500 transition-colors"
              >
                Sign in here
              </button>
            </p>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default QuickSignup